import { EffectExecutor } from './chunk-E25Z7AVU.js';
export { EffectExecutor, EventBus, HANDLER_MANIFEST, InMemoryPersistence, LIFECYCLE_EVENTS, MockPersistenceAdapter, StateMachineManager, TickScheduler, buildEmitsFromTraits, buildSourceMatcher, collectDeclaredConfigDefaults, collectDeclaredEntityDefaults, createInitialTraitState, createMockPersistence, createTestExecutor, createTickScheduler, createUnifiedLoader, eventRouteKey, findEntityAmongOrbitals, findInitialState, findTransition, formatPayloadValidationError, getIsolatedCollectionName, getNamespacedEvent, isBrowser, isElectron, isNamespacedEvent, isNode, isValidDurationString, normalizeCallSiteConfigToValues, normalizeEventKey, parseDurationString, parseListenSource, parseNamespacedEvent, parseOrbitalTraits, preprocessSchema, processEvent, runTraitCascade, selectDispatchCandidates, validateEventPayload, validatePayloadShapes } from './chunk-E25Z7AVU.js';
export { cronMatches, cronMinuteKey, isValidCronExpression, parseCron, parseCronField } from './chunk-OU3ITB5S.js';
import { createContextFromBindings, applyRowAccess, checkMutationAccess, accessDeniedMessage } from './chunk-ZWAOJT6R.js';
export { applyRowAccess, checkMutationAccess, containsBindings, createContextFromBindings, createMinimalContext, deferEntityBindings, extractBindings, interpolateProps, interpolateValue } from './chunk-ZWAOJT6R.js';
import './chunk-T4VDAB4C.js';
export { PERF_NAMESPACE, RendererContractViolationError, adjustSchemaForMockData, aggregateSlotContent, assertIsMultiSourceSlotManager, assertIsSlotManager, bindEventBus, bindTraitStateGetter, buildMockData, buildOrbitalsByTrait, clearPerf, createSlotSetter, ensureVerificationApi, getOrbitalVerification, perfEnd, perfGauge, perfStart, perfStore, perfTime, perfTimeAsync, prepareSchemaForPreview, pushPerfEntry, validateSlotContent, wrapCallbackForEvent } from './chunk-F5W3YZGJ.js';
export { collectEmbeddedTraits, collectTraitRefsFromResolvedTrait } from './chunk-SCRAHWOC.js';
import { __export } from './chunk-MLKGABMK.js';
import { createLogger } from '@almadar/logger';
import { isInlineTrait, isPersistBatchOperation } from '@almadar/core';
import { evaluate } from '@almadar/evaluator';

var log = createLogger("almadar:runtime:effects:client");
var _ws = null;
var _wsQueue = [];
function getOrOpenWs() {
  if (typeof WebSocket === "undefined" || typeof location === "undefined") return null;
  if (_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING)) {
    return _ws;
  }
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const url = `${protocol}//${location.host}/ws/events`;
  try {
    _ws = new WebSocket(url);
    _ws.addEventListener("open", () => {
      const queued = _wsQueue.splice(0);
      for (const msg of queued) {
        _ws.send(msg);
      }
    });
    _ws.addEventListener("error", () => {
      _ws = null;
    });
    _ws.addEventListener("close", () => {
      _ws = null;
    });
  } catch {
    _ws = null;
  }
  return _ws;
}
function urlBase64ToArrayBuffer(base64Url) {
  const padding = "=".repeat((4 - base64Url.length % 4) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) {
    view[i] = raw.charCodeAt(i);
  }
  return buffer;
}
function sendServerEvent(orbital, event, payload) {
  const msg = JSON.stringify({
    type: "ORBITAL_EVENT",
    payload: { orbital, event, payload: payload ?? null }
  });
  const ws = getOrOpenWs();
  if (!ws) {
    log.warn("send-server:no-ws", { event });
    return;
  }
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(msg);
  } else {
    _wsQueue.push(msg);
  }
}
function createClientEffectHandlers(options) {
  const { eventBus, slotSetter, navigate, navigateBack, sendServer, orbitalName = "" } = options;
  return {
    emit: (event, payload) => {
      const prefixedEvent = event.startsWith("UI:") ? event : `UI:${event}`;
      eventBus.emit(prefixedEvent, payload);
    },
    persist: async () => {
      log.warn("persist-server-side-only");
    },
    set: () => {
      log.warn("set-server-side-only");
    },
    callService: async () => {
      log.warn("call-service-server-side-only");
      return {};
    },
    renderUI: (slot, pattern, props) => {
      if (pattern === null) {
        slotSetter.clearSlot(slot);
        return;
      }
      slotSetter.addPattern(slot, pattern, props);
    },
    navigate: navigate ?? ((path) => {
      if (typeof window !== "undefined" && /^https?:\/\//.test(path)) {
        window.location.href = path;
        return;
      }
      log.warn("navigate-no-handler", { path });
    }),
    navigateBack: navigateBack ?? (() => {
      log.warn("navigate-back-no-handler");
    }),
    sendServer: sendServer ?? ((event, payload) => {
      sendServerEvent(orbitalName, event, payload);
    }),
    // === Browser device handlers (client host path) ===
    // Each throws when the underlying API is unavailable so the executor's
    // runSubstrate wrapper fires `emit.failure` with `{ error }`.
    browserOpenFilePicker: async (options2) => {
      if (typeof window === "undefined" || !("showOpenFilePicker" in window)) {
        throw new Error("File picker API is not available in this environment");
      }
      const host = window;
      const pickerOptions = {};
      if (options2?.multiple === true) pickerOptions.multiple = true;
      if (typeof options2?.accept === "string" && options2.accept.length > 0) {
        pickerOptions.types = [{ accept: { [options2.accept]: [] } }];
      }
      const handles = await host.showOpenFilePicker(pickerOptions);
      const files = await Promise.all(handles.map(async (handle) => {
        const file = await handle.getFile();
        return { name: file.name, size: file.size, type: file.type, lastModified: file.lastModified };
      }));
      return { files };
    },
    browserClipboardRead: async () => {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API is not available in this environment");
      }
      const text = await navigator.clipboard.readText();
      return { text };
    },
    browserClipboardWrite: async (text) => {
      if (typeof navigator === "undefined" || !navigator.clipboard) {
        throw new Error("Clipboard API is not available in this environment");
      }
      await navigator.clipboard.writeText(text);
      return { text };
    },
    browserPushSubscribe: async () => {
      if (typeof navigator === "undefined" || !("serviceWorker" in navigator) || typeof window === "undefined" || !("PushManager" in window)) {
        throw new Error("Push API is not available in this environment");
      }
      const keyResponse = await fetch("/api/push/vapid-public-key");
      if (!keyResponse.ok) {
        throw new Error("Push is not configured on this host (no VAPID public key)");
      }
      const { publicKey } = await keyResponse.json();
      const registration = await navigator.serviceWorker.register("/almadar-push-sw.js");
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(publicKey)
      });
      const json = subscription.toJSON();
      const endpoint = json.endpoint ?? "";
      const p256dh = json.keys?.p256dh ?? "";
      const auth = json.keys?.auth ?? "";
      if (!endpoint || !p256dh || !auth) {
        throw new Error("Push subscription resolved without endpoint/keys");
      }
      return { endpoint, p256dh, auth };
    },
    browserGeolocationCurrent: async (options2) => {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        throw new Error("Geolocation API is not available in this environment");
      }
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, options2);
      });
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
    }
  };
}
var NO_ROW_KEY_IS_FATAL = true;
var effectLog = createLogger("almadar:runtime:effects");
function createServerEffectHandlers(opts) {
  const {
    persistence,
    eventBus,
    entityType,
    entityId,
    bindings,
    context,
    effectResults,
    fetchedData,
    emittedEvents,
    clientEffects,
    clientEffectsByTrait,
    source,
    callService: consumerCallService,
    entityAccess,
    debug
  } = opts;
  const pushClientEffect = (effect) => {
    clientEffects?.push(effect);
    clientEffectsByTrait?.push({ traitName: context?.traitName ?? "", effect });
  };
  const accessBindings = (forEntityType) => ({
    user: bindings?.user,
    payload: bindings?.payload,
    config: bindings?.config,
    onPredicateError: (err) => {
      effectLog.error("access-predicate-eval-error", {
        entityType: forEntityType,
        error: err instanceof Error ? err : String(err)
      });
    }
  });
  const record = (entry) => {
    effectResults?.push(entry);
  };
  const handlers = {
    emit: (event, eventPayload, emitSource) => {
      if (debug) {
        effectLog.debug("emit", {
          event,
          payloadKeys: eventPayload ? Object.keys(eventPayload).join(",") : ""
        });
      }
      const stamp = emitSource ?? source;
      eventBus.emit(event, eventPayload, stamp);
      emittedEvents?.push({ event, payload: eventPayload });
      effectLog.debug("emit:push", {
        event,
        cumulativeEmittedCount: emittedEvents?.length,
        sourceTrait: stamp?.trait,
        sourceOrbital: stamp?.orbital,
        emittedEventsBound: emittedEvents !== void 0
      });
    },
    set: async (targetId, field, value) => {
      const id = targetId || entityId;
      if (!id) return;
      try {
        await persistence.update(entityType, id, { [field]: value });
        record({
          effect: "set",
          entityType,
          data: { id, field, value },
          success: true
        });
      } catch (err) {
        record({
          effect: "set",
          entityType,
          data: { id, field, value },
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    },
    persist: async (action, targetEntityType, data) => {
      if (action === "batch") {
        const operations = data?.operations;
        if (!Array.isArray(operations) || operations.length === 0) {
          record({
            effect: "persist",
            action: "batch",
            success: false,
            error: "Batch requires a non-empty operations array"
          });
          return;
        }
        const batchResults = [];
        const completed = [];
        let batchFailed = false;
        let batchError = "";
        const ops = operations;
        for (const op of ops) {
          if (!isPersistBatchOperation(op)) {
            batchFailed = true;
            batchError = `Invalid batch operation format: ${JSON.stringify(op)}`;
            break;
          }
          const opAction = op[0];
          const opEntityType = op[1];
          try {
            switch (op[0]) {
              case "create": {
                const createData = op[2] ?? {};
                const { id: newId } = await persistence.create(
                  opEntityType,
                  createData
                );
                batchResults.push({
                  ...createData,
                  action: "create",
                  entityType: opEntityType,
                  id: newId
                });
                completed.push({
                  action: "create",
                  entityType: opEntityType,
                  id: newId
                });
                break;
              }
              case "update": {
                const updateId = op[2];
                const updateData = op[3] ?? {};
                await persistence.update(opEntityType, updateId, updateData);
                const updated = await persistence.getById(opEntityType, updateId);
                batchResults.push({
                  ...updated || updateData,
                  action: "update",
                  entityType: opEntityType,
                  id: updateId
                });
                completed.push({
                  action: "update",
                  entityType: opEntityType,
                  id: updateId
                });
                break;
              }
              case "delete": {
                const deleteId = op[2];
                await persistence.delete(opEntityType, deleteId);
                batchResults.push({
                  action: "delete",
                  entityType: opEntityType,
                  id: deleteId,
                  deleted: true
                });
                completed.push({
                  action: "delete",
                  entityType: opEntityType,
                  id: deleteId
                });
                break;
              }
              default:
                batchFailed = true;
                batchError = `Unknown batch operation action: ${opAction}`;
                break;
            }
          } catch (err) {
            batchFailed = true;
            batchError = `Batch operation [${opAction}, ${opEntityType}] failed: ${err instanceof Error ? err.message : String(err)}`;
            break;
          }
          if (batchFailed) break;
        }
        const batchSummary = {
          operations: batchResults,
          completedCount: completed.length,
          totalCount: operations.length
        };
        record({
          effect: "persist",
          action: "batch",
          data: batchSummary,
          success: !batchFailed,
          ...batchFailed ? { error: batchError } : {}
        });
        return batchSummary;
      }
      const type = targetEntityType || entityType;
      let resultData;
      const sizeBefore = (await persistence.list(type)).length;
      let deniedReason;
      try {
        const mutationPolicy = entityAccess?.get(type)?.[action];
        switch (action) {
          case "create": {
            if (!checkMutationAccess(data ?? {}, mutationPolicy, accessBindings(type))) {
              deniedReason = "access-denied";
              throw new Error(accessDeniedMessage("create", type));
            }
            const { id } = await persistence.create(type, data ?? {});
            resultData = { ...data ?? {}, id };
            break;
          }
          case "update": {
            const row = data ?? {};
            const idOrFallback = row.id ?? entityId;
            if (idOrFallback) {
              if (mutationPolicy !== void 0) {
                const existing = await persistence.getById(type, idOrFallback);
                if (!existing || !checkMutationAccess(existing, mutationPolicy, accessBindings(type))) {
                  deniedReason = "access-denied";
                  throw new Error(accessDeniedMessage("update", type));
                }
              }
              await persistence.update(type, idOrFallback, row);
              const updated = await persistence.getById(type, idOrFallback);
              resultData = updated ?? { ...row, id: idOrFallback };
            } else {
              effectLog.error("persist:no-row-key", { action, entityType: type });
              if (NO_ROW_KEY_IS_FATAL) {
                deniedReason = "no-row-key";
                throw new Error(
                  `persist ${action} ${type} resolved no row key \u2014 the id was neither on the row being written nor on the request. Bind it before the write, e.g. (set @entity.id ?row.id) on the transition that selects it.`
                );
              }
            }
            break;
          }
          case "delete": {
            const directId = typeof data === "string" ? data : void 0;
            const nestedId = typeof data === "object" && data !== null ? data.id : void 0;
            const deleteId = directId ?? nestedId ?? entityId;
            if (deleteId) {
              if (mutationPolicy !== void 0) {
                const existing = await persistence.getById(type, deleteId);
                if (!existing || !checkMutationAccess(existing, mutationPolicy, accessBindings(type))) {
                  deniedReason = "access-denied";
                  throw new Error(accessDeniedMessage("delete", type));
                }
              }
              await persistence.delete(type, deleteId);
              resultData = { id: deleteId, deleted: true };
            } else {
              effectLog.error("persist:no-row-key", { action, entityType: type });
              if (NO_ROW_KEY_IS_FATAL) {
                deniedReason = "no-row-key";
                throw new Error(
                  `persist ${action} ${type} resolved no row key \u2014 the id was neither on the row being written nor on the request. Bind it before the write, e.g. (set @entity.id ?row.id) on the transition that selects it.`
                );
              }
            }
            break;
          }
        }
        if (resultData === void 0 && (action === "update" || action === "delete")) {
          record({
            effect: "persist",
            action,
            entityType: type,
            success: false,
            denied: true,
            error: `persist ${action} ${type} resolved no row key`
          });
          return void 0;
        }
        const sizeAfter = (await persistence.list(type)).length;
        effectLog.debug("persist:store-mutate", {
          action,
          entityType: type,
          resultId: resultData?.id,
          sizeBefore,
          sizeAfter,
          delta: sizeAfter - sizeBefore
        });
        record({
          effect: "persist",
          action,
          entityType: type,
          data: resultData,
          success: true
        });
        return resultData;
      } catch (err) {
        effectLog.error("persist:store-mutate-error", {
          action,
          entityType: type,
          error: err instanceof Error ? err.message : String(err)
        });
        record({
          effect: "persist",
          action,
          entityType: type,
          ...deniedReason !== void 0 ? { denied: true } : {},
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
      }
      return void 0;
    },
    callService: async (service, action, params) => {
      try {
        let result = null;
        if (consumerCallService) {
          const viewer = bindings?.user;
          result = await consumerCallService(
            service,
            action,
            params,
            viewer ? { principal: viewer.id, role: viewer.role } : void 0
          );
        } else {
          const mockId = `mock_${service}_${action}_${Math.random().toString(36).slice(2, 10)}`;
          const paramsEcho = {};
          if (params && typeof params === "object") {
            for (const [k, v] of Object.entries(params)) {
              if (v !== void 0 && (typeof v === "string" || typeof v === "number" || typeof v === "boolean" || v === null || v instanceof Date)) {
                paramsEcho[k] = v;
              }
            }
          }
          result = {
            id: mockId,
            clientSecret: `secret_${mockId}`,
            success: true,
            status: "succeeded",
            ...paramsEcho
          };
          if (debug) {
            effectLog.warn("call-service-not-configured-using-mock", { service, action });
          }
        }
        record({
          effect: "call-service",
          action: `${service}.${action}`,
          data: result,
          success: true
        });
        return result;
      } catch (err) {
        record({
          effect: "call-service",
          action: `${service}.${action}`,
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
        return null;
      }
    },
    fetch: async (fetchEntityType, options) => {
      try {
        let result = null;
        let total = 0;
        if (options?.id) {
          const entity = await persistence.getById(fetchEntityType, options.id);
          const visible = entity ? applyRowAccess(
            [entity],
            entityAccess?.get(fetchEntityType)?.read,
            void 0,
            accessBindings(fetchEntityType)
          ) : [];
          if (visible.length > 0) {
            if (fetchedData) fetchedData[fetchEntityType] = visible;
            result = visible[0] ?? null;
            total = 1;
          }
        } else {
          let entities = await persistence.list(fetchEntityType);
          entities = applyRowAccess(
            entities,
            entityAccess?.get(fetchEntityType)?.read,
            options?.filter,
            accessBindings(fetchEntityType)
          );
          total = entities.length;
          if (options?.offset && options.offset > 0) {
            entities = entities.slice(options.offset);
          }
          if (options?.limit && options.limit > 0) {
            entities = entities.slice(0, options.limit);
          }
          if (fetchedData) fetchedData[fetchEntityType] = entities;
          result = entities;
        }
        return result === null ? null : { rows: result, total };
      } catch (err) {
        effectLog.error("fetch-error", {
          entityType: fetchEntityType,
          error: err instanceof Error ? err : String(err)
        });
        return null;
      }
    },
    fetchStream: async (streamEntityType, options, onChunk) => {
      try {
        const rows = await persistence.list(streamEntityType);
        const visible = applyRowAccess(
          rows,
          entityAccess?.get(streamEntityType)?.read,
          void 0,
          accessBindings(streamEntityType)
        );
        const matched = options?.id ? visible.filter((r) => r["id"] === options.id) : visible;
        for (const row of matched) {
          onChunk(row);
        }
        return matched;
      } catch (err) {
        effectLog.error("fetch-stream-error", {
          entityType: streamEntityType,
          error: err instanceof Error ? err : String(err)
        });
        return null;
      }
    },
    ref: async (refEntityType, options) => {
      return handlers.fetch(refEntityType, options);
    },
    deref: async (derefEntityType, options) => {
      try {
        let result = null;
        let total = 0;
        if (options?.id) {
          const entity = await persistence.getById(derefEntityType, options.id);
          if (entity) {
            if (fetchedData) fetchedData[derefEntityType] = [entity];
            result = entity;
            total = 1;
          }
        } else {
          const entities = await persistence.list(derefEntityType);
          if (fetchedData) fetchedData[derefEntityType] = entities;
          result = entities;
          total = entities.length;
        }
        record({
          effect: "deref",
          entityType: derefEntityType,
          success: true
        });
        return result === null ? null : { rows: result, total };
      } catch (err) {
        record({
          effect: "deref",
          entityType: derefEntityType,
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
        return null;
      }
    },
    swap: async (swapEntityType, swapEntityId, transform) => {
      try {
        const current = await persistence.getById(swapEntityType, swapEntityId);
        if (!current) {
          record({
            effect: "swap!",
            entityType: swapEntityType,
            success: false,
            error: `Entity ${swapEntityType}/${swapEntityId} not found`
          });
          return null;
        }
        const ctx = createContextFromBindings(
          { current, entity: bindings?.entity, payload: bindings?.payload },
          false
        );
        let newData;
        if (Array.isArray(transform)) {
          const evalResult = evaluate(transform, ctx);
          if (evalResult && typeof evalResult === "object" && !Array.isArray(evalResult)) {
            newData = evalResult;
          } else {
            newData = current;
          }
        } else if (typeof transform === "object" && transform !== null) {
          newData = { ...current, ...transform };
        } else {
          record({
            effect: "swap!",
            entityType: swapEntityType,
            success: false,
            error: "swap! transform must be an S-expression or object"
          });
          return null;
        }
        await persistence.update(swapEntityType, swapEntityId, newData);
        record({
          effect: "swap!",
          entityType: swapEntityType,
          data: { id: swapEntityId, ...newData },
          success: true
        });
        return newData;
      } catch (err) {
        record({
          effect: "swap!",
          entityType: swapEntityType,
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
        return null;
      }
    },
    watch: (_watchEntityType) => {
    },
    atomic: async (atomicEffects) => {
      const atomicExecutor = new EffectExecutor({
        handlers,
        bindings: bindings ?? {},
        context: context ?? {
          traitName: "atomic",
          state: "unknown",
          transition: "unknown"
        }
      });
      try {
        await atomicExecutor.executeAll(atomicEffects);
        record({ effect: "atomic", success: true });
      } catch (err) {
        record({
          effect: "atomic",
          success: false,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    },
    renderUI: (slot, pattern, props, priority) => {
      pushClientEffect(["render-ui", slot, pattern, props, priority]);
    },
    navigate: (path, params, crumb) => {
      if (crumb !== void 0) {
        pushClientEffect(["navigate", path, params, { crumb }]);
      } else {
        pushClientEffect(["navigate", path, params]);
      }
    },
    navigateBack: () => {
      pushClientEffect(["navigate-back"]);
    }
  };
  return handlers;
}

// src/composition/index.ts
var composition_exports = {};
__export(composition_exports, {
  applyEventWiring: () => applyEventWiring,
  composeBehaviors: () => composeBehaviors,
  detectLayoutStrategy: () => detectLayoutStrategy,
  pipeBehaviors: () => pipeBehaviors
});
function findInlineTrait(orbitals, name) {
  for (const orbital of orbitals) {
    for (const traitRef of orbital.traits) {
      if (isInlineTrait(traitRef) && traitRef.name === name) {
        return traitRef;
      }
    }
  }
  return null;
}
function hasEmit(emits, event) {
  return emits.some((e) => e.event === event);
}
function hasListen(listens, event, triggers) {
  return listens.some((l) => l.event === event && l.triggers === triggers);
}
function applyEventWiring(orbitals, wiring) {
  const cloned = JSON.parse(
    JSON.stringify(orbitals)
  );
  for (const entry of wiring) {
    const sourceTrait = findInlineTrait(cloned, entry.from);
    if (sourceTrait) {
      if (!sourceTrait.emits) {
        sourceTrait.emits = [];
      }
      if (!hasEmit(sourceTrait.emits, entry.event)) {
        sourceTrait.emits.push({
          event: entry.event,
          scope: "external"
        });
      }
    }
    const targetTrait = findInlineTrait(cloned, entry.to);
    if (targetTrait) {
      if (!targetTrait.listens) {
        targetTrait.listens = [];
      }
      if (!hasListen(targetTrait.listens, entry.event, entry.triggers)) {
        targetTrait.listens.push({
          event: entry.event,
          triggers: entry.triggers,
          scope: "external"
        });
      }
    }
  }
  return cloned;
}

// src/composition/layout-strategy.ts
function hasSequentialChain(wiring) {
  if (wiring.length < 2) {
    return false;
  }
  const adjacency = /* @__PURE__ */ new Map();
  const allTargets = /* @__PURE__ */ new Set();
  for (const entry of wiring) {
    let targets = adjacency.get(entry.from);
    if (!targets) {
      targets = /* @__PURE__ */ new Set();
      adjacency.set(entry.from, targets);
    }
    targets.add(entry.to);
    allTargets.add(entry.to);
  }
  const roots = [];
  for (const from of adjacency.keys()) {
    if (!allTargets.has(from)) {
      roots.push(from);
    }
  }
  const starts = roots.length > 0 ? roots : [...adjacency.keys()];
  for (const start of starts) {
    let current = start;
    let length = 1;
    const visited = /* @__PURE__ */ new Set([current]);
    while (true) {
      const nexts = adjacency.get(current);
      if (!nexts || nexts.size === 0) break;
      let advanced = false;
      for (const next of nexts) {
        if (!visited.has(next)) {
          visited.add(next);
          current = next;
          length++;
          advanced = true;
          break;
        }
      }
      if (!advanced) break;
    }
    if (length >= 3) {
      return true;
    }
  }
  return false;
}
function detectLayoutStrategy(orbitals, eventWiring) {
  if (eventWiring && eventWiring.length > 0 && hasSequentialChain(eventWiring)) {
    return "wizard-flow";
  }
  const count = orbitals.length;
  if (count <= 1) {
    return "single";
  }
  if (count <= 4) {
    return "tabs";
  }
  return "sidebar";
}

// src/composition/compose-behaviors.ts
function isSchema(input) {
  return "orbitals" in input && Array.isArray(input.orbitals);
}
function asDefinitions(inputs) {
  return inputs.flatMap(
    (input) => isSchema(input) ? input.orbitals : [input]
  );
}
function mergeLedgers(inputs) {
  const merged = /* @__PURE__ */ new Map();
  let sawLedger = false;
  for (const input of inputs) {
    if (!isSchema(input) || input.ledger === void 0) continue;
    sawLedger = true;
    for (const [id, entry] of Object.entries(input.ledger.entries)) {
      if (!merged.has(id)) merged.set(id, entry);
    }
  }
  if (!sawLedger) return void 0;
  const entries = {};
  for (const [id, entry] of [...merged.entries()].sort(
    ([a], [b]) => a < b ? -1 : a > b ? 1 : 0
  )) {
    entries[id] = entry;
  }
  return { schemaVersion: 1, entries };
}
function mergeSchemaVersions(inputs) {
  let max;
  for (const input of inputs) {
    if (!isSchema(input) || input.schemaVersion === void 0) continue;
    max = max === void 0 ? input.schemaVersion : Math.max(max, input.schemaVersion);
  }
  return max;
}
function toKebabCase(name) {
  return name.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[\s_]+/g, "-").toLowerCase();
}
function generatePages(orbitals, strategy) {
  switch (strategy) {
    case "single": {
      const orbital = orbitals[0];
      const name = orbital?.name ?? "Main";
      return [
        {
          name: `${name}Page`,
          path: "/",
          isInitial: true,
          primaryEntity: getEntityName(orbital)
        }
      ];
    }
    case "dashboard": {
      return [
        {
          name: "DashboardPage",
          path: "/",
          viewType: "dashboard",
          isInitial: true
        }
      ];
    }
    case "sidebar":
    case "tabs":
    case "wizard-flow": {
      return orbitals.map((orbital, index) => ({
        name: `${orbital.name}Page`,
        path: index === 0 ? "/" : `/${toKebabCase(orbital.name)}`,
        isInitial: index === 0,
        primaryEntity: getEntityName(orbital)
      }));
    }
  }
}
function getEntityName(orbital) {
  if (!orbital) return void 0;
  const entity = orbital.entity;
  if (typeof entity === "string") {
    return entity.replace(".entity", "");
  }
  return entity.name;
}
function composeBehaviors(input) {
  const {
    appName,
    orbitals: rawInputs,
    layoutStrategy: strategyInput,
    eventWiring
  } = input;
  const rawOrbitals = asDefinitions(rawInputs);
  const wiredOrbitals = eventWiring && eventWiring.length > 0 ? applyEventWiring(rawOrbitals, eventWiring) : rawOrbitals;
  const strategy = !strategyInput || strategyInput === "auto" ? detectLayoutStrategy(wiredOrbitals, eventWiring) : strategyInput;
  const pages = generatePages(wiredOrbitals, strategy);
  const orbitalsWithPages = wiredOrbitals.map((orbital, index) => {
    if (orbital.pages && orbital.pages.length > 0) {
      return orbital;
    }
    const page = strategy === "dashboard" || strategy === "single" ? pages[0] : pages[index];
    return {
      ...orbital,
      pages: page ? [page] : []
    };
  });
  const ledger = mergeLedgers(rawInputs);
  const schemaVersion = mergeSchemaVersions(rawInputs);
  const schema = {
    name: appName,
    version: "1.0.0",
    orbitals: orbitalsWithPages,
    ...schemaVersion !== void 0 ? { schemaVersion } : {},
    ...ledger !== void 0 ? { ledger } : {}
  };
  return {
    schema,
    layout: {
      strategy,
      pageCount: pages.length
    },
    wiring: {
      connections: eventWiring?.length ?? 0
    }
  };
}

// src/composition/pipe.ts
function pipeBehaviors(seed, ...steps) {
  let current = seed;
  for (const step of steps) {
    current = step(current);
  }
  return current;
}

export { applyEventWiring, composeBehaviors, composition_exports as composition, createClientEffectHandlers, createServerEffectHandlers, detectLayoutStrategy, pipeBehaviors };
